import type { TrustedContactType } from "../../types/trusted-contacts";

/**
 * How each contact type is spelled on screen.
 *
 * The wire values are PascalCase enum members; nobody says "JucoCoach". Kept in one place so the
 * filter, the table badge and the add/edit form cannot drift into three spellings of one thing.
 */
export const CONTACT_TYPE_LABELS: Record<TrustedContactType, string> = {
  CollegeCoach: "College Coach",
  JucoCoach: "JUCO Coach",
  HighSchoolCoach: "High School Coach",
  TravelCoach: "Travel Coach",
  Scout: "Scout",
};

/**
 * Badge modifier per type. Colour is a scanning aid, never the only signal — the label is always
 * rendered beside it, so a coach who cannot tell the two blues apart reads the same table.
 */
export const CONTACT_TYPE_MODIFIER: Record<TrustedContactType, string> = {
  CollegeCoach: "college",
  JucoCoach: "juco",
  HighSchoolCoach: "high-school",
  TravelCoach: "travel",
  Scout: "scout",
};

/**
 * The label the type filter and form show, given a wire value.
 *
 * The `??` is not dead weight: the union is a mirror of a server-side enum, so a build that is
 * behind a deploy can legitimately receive a sixth member. Showing its raw wire value beats
 * showing nothing. `CONTACT_TYPE_MODIFIER` is read with the same fallback for the same reason.
 */
export const contactTypeLabel = (type: TrustedContactType): string =>
  CONTACT_TYPE_LABELS[type] ?? type;

/** The wire value behind a label, for controls that work in display copy. */
export const contactTypeFromLabel = (
  label: string
): TrustedContactType | null => {
  const entry = Object.entries(CONTACT_TYPE_LABELS).find(([, l]) => l === label);
  return entry ? (entry[0] as TrustedContactType) : null;
};
